import { useState } from "react";
import axios from "axios";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../ui/dialog";
import { useLang } from "../../context/LangContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", email: "", company: "", role: "", problem: "", website: "", data_types: "", timeline: "", budget: "" };
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const Field = ({ id, label, error, children }) => (
  <div className="flex flex-col gap-2">
    <label htmlFor={id} className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/60">{label}</label>
    {children}
    {error && <p id={`${id}-error`} role="alert" data-testid={`${id}-error`} className="text-xs text-[#e8c979]">{error}</p>}
  </div>
);

export const RequestForm = ({ offer, onDone }) => {
  const { t, lang } = useLang();
  const r = t.request;
  const f = r.fields;
  const [data, setData] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setData({ ...data, [k]: e.target.value });
  const early = offer === "early_access";
  const consulting = offer === "consulting";
  const p = `req-${offer}`;

  const validate = () => {
    const e = {};
    for (const k of ["name", "company", "role"]) if (!data[k].trim()) e[k] = r.errors.required;
    if (!EMAIL_RE.test(data.email)) e.email = r.errors.email;
    if (data.problem.trim().length < 10) e.problem = r.errors.min;
    if (consulting && !data.budget) e.budget = r.errors.required;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const payload = { offer, name: data.name, email: data.email, company: data.company, role: data.role, problem: data.problem, language: lang,
        website: data.website || null, data_types: consulting ? data.data_types || null : null, timeline: consulting ? data.timeline || null : null, budget: consulting ? data.budget : null };
      await axios.post(`${API}/requests`, payload);
      onDone();
    } catch {
      setErrors({ form: r.err });
    } finally {
      setLoading(false);
    }
  };

  const input = (k, label, type = "text", extra = {}) => (
    <Field id={`${p}-${k}`} label={label} error={errors[k]}>
      <input id={`${p}-${k}`} data-testid={`${p}-${k}-input`} type={type} className="field" value={data[k]} onChange={set(k)} aria-invalid={!!errors[k]} aria-describedby={errors[k] ? `${p}-${k}-error` : undefined} {...extra} />
    </Field>
  );

  return (
    <form onSubmit={submit} noValidate data-testid={`${p}-form`} className="grid gap-5 sm:grid-cols-2">
      {input("name", f.name, "text", { autoComplete: "name" })}
      {input("email", f.email, "email", { autoComplete: "email" })}
      {input("company", early ? f.organization : f.company, "text", { autoComplete: "organization" })}
      {input("role", f.role, "text", { autoComplete: "organization-title" })}
      <div className="sm:col-span-2">
        <Field id={`${p}-problem`} label={early ? f.interest : f.problem} error={errors.problem}>
          <textarea id={`${p}-problem`} data-testid={`${p}-problem-input`} rows={4} className="field resize-none" value={data.problem} onChange={set("problem")} aria-invalid={!!errors.problem} />
        </Field>
      </div>
      {!early && input("website", f.website, "url", { autoComplete: "url", placeholder: "https://" })}
      {consulting && (
        <>
          {input("data_types", f.data_types)}
          {input("timeline", f.timeline)}
          <Field id={`${p}-budget`} label={f.budget} error={errors.budget}>
            <select id={`${p}-budget`} data-testid={`${p}-budget-select`} className="field cursor-pointer [&>option]:bg-[#0a0a0b]" value={data.budget} onChange={set("budget")} aria-invalid={!!errors.budget}>
              <option value="">—</option>
              {Object.entries(f.budgets).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </Field>
        </>
      )}
      <div className="flex flex-col gap-4 sm:col-span-2">
        <p className="text-xs leading-relaxed text-white/55">{r.noDocs} {r.consent}</p>
        {errors.form && <p role="alert" data-testid={`${p}-form-error`} className="text-sm text-[#e8c979]">{errors.form}</p>}
        <button type="submit" disabled={loading} data-testid={`${p}-submit-button`} className="btn-gold group w-full justify-center disabled:opacity-70 sm:w-fit">
          {loading ? <><Loader2 className="h-4 w-4 animate-spin" />{r.sending}</> : <>{r.submit}<ArrowRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-1" /></>}
        </button>
      </div>
    </form>
  );
};

export const RequestDialog = ({ offer, onClose }) => {
  const { t } = useLang();
  const r = t.request;
  const [done, setDone] = useState(false);
  const close = () => { setDone(false); onClose(); };
  return (
    <Dialog open={!!offer} onOpenChange={(o) => !o && close()}>
      <DialogContent data-lenis-prevent data-testid={`request-modal-${offer || "none"}`} className="max-h-[92vh] max-w-2xl overflow-y-auto rounded-[28px] border border-[#d4af37]/25 bg-[#0d0d0f] p-6 text-[#fafaf7] sm:rounded-[28px] md:p-12 [&>button]:text-white/60">
        {offer && !done && (
          <>
            <DialogTitle className="font-display text-2xl font-bold tracking-tight md:text-4xl">{r.titles[offer]}</DialogTitle>
            <DialogDescription className="mb-6 text-sm leading-relaxed text-white/65 md:text-base">{r.subs[offer]}</DialogDescription>
            <RequestForm offer={offer} onDone={() => setDone(true)} />
          </>
        )}
        {offer && done && (
          <div className="py-6 text-center" data-testid="request-success">
            <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full border border-[#d4af37]/30 bg-[#d4af37]/[0.08]"><CheckCircle2 className="h-10 w-10 text-[#e8c979]" /></div>
            <DialogTitle className="mt-6 font-display text-2xl font-bold md:text-3xl">{r.doneTitle}</DialogTitle>
            <DialogDescription className="mt-3 text-sm text-white/65 md:text-base">{r.doneText}</DialogDescription>
            <button data-testid="request-close-button" onClick={close} className="btn-ghost mx-auto mt-8">{r.close}</button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
